<template>
  <div class="ai-knowledge-page">
    <div class="toolbar title-block">
      <h1>База знаний</h1>
      <p class="subtitle">Документы, правила и заметки AI-ассистента — с тегами и поиском</p>
    </div>

    <div v-if="loadError" class="error">{{ loadError }}</div>

    <section class="card">
      <header class="card-header">
        <div class="kind-tabs">
          <button
            v-for="tab in kindTabs"
            :key="tab.kind"
            class="kind-tab"
            :class="{ active: activeKind === tab.kind }"
            @click="switchKind(tab.kind)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="header-actions">
          <input
            v-model="q"
            class="kb-search"
            placeholder="Поиск по заголовку и тексту…"
            maxlength="200"
          >
          <button class="btn" :disabled="loading" @click="reload">
            {{ loading ? 'Загрузка…' : 'Обновить' }}
          </button>
          <button
            v-if="canManage || activeKind === 'personal'"
            class="btn primary"
            :disabled="busy || editorOpen"
            @click="openEditor(null)"
          >
            + Новый документ
          </button>
        </div>
      </header>

      <div v-if="popularTags.length" class="tag-row">
        <span class="tag-row-label">Теги:</span>
        <button
          v-for="t in popularTags"
          :key="t.tag"
          class="tag-chip"
          :class="{ active: selectedTags.includes(t.tag) }"
          @click="toggleTag(t.tag)"
        >
          {{ t.tag }} <span class="tag-count">{{ t.count }}</span>
        </button>
        <button
          v-if="selectedTags.length"
          class="kb-act tag-clear"
          @click="clearTags"
        >
          сбросить
        </button>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <div v-if="selection.length" class="bulk-bar">
        <span>Выбрано: {{ selection.length }}</span>
        <button class="btn danger" :disabled="busy" @click="bulkDelete">
          Удалить выбранные
        </button>
        <button class="btn" @click="clearSelection">Сбросить</button>
      </div>

      <div v-if="loading" class="kb-loading">
        <span class="kb-loading-spinner"></span>
        Загрузка…
      </div>

      <div v-if="!items.length && !loading && !editorOpen" class="field-hint empty-hint">
        {{ q || selectedTags.length ? 'Ничего не найдено' : 'Пока пусто' }}
      </div>

      <KnowledgeEditor
        v-if="editorOpen && !editingId"
        :can-manage="canManage"
        :busy="busy"
        :popular-tags="popularTags"
        :initial="draft"
        @save="saveEditor"
        @cancel="closeEditor"
      />

      <template v-if="!loading">
        <div
          v-for="item in items"
          :key="item.id"
          class="kb-item"
          :class="{ editing: editingId === item.id }"
        >
        <div class="kb-item-top" @click="openItem(item)">
          <label v-if="canDelete(item)" class="kb-check" @click.stop>
            <input
              type="checkbox"
              :checked="selection.includes(item.id)"
              @change="toggleSelect(item.id)"
            >
          </label>
          <span class="kb-item-title">
            {{ item.title || item.text.slice(0, 80) || '(без заголовка)' }}
          </span>
          <span v-if="item.pinned" class="badge">закреплена</span>
          <span
            v-for="tag in item.tags || []"
            :key="tag"
            class="tag-chip mini"
            @click.stop="toggleTag(tag)"
          >{{ tag }}</span>
          <span class="kb-item-date">{{ fmtDate(item) }}</span>
          <span class="kb-item-actions" @click.stop>
            <button
              v-if="canPin(item)"
              class="kb-icon"
              :class="{ active: item.pinned }"
              :disabled="busy"
              :title="item.pinned ? 'Открепить' : 'Закрепить в промпте'"
              @click="togglePin(item)"
            >
              <font-awesome-icon :icon="['fa-solid', 'fa-thumbtack']" />
            </button>
            <button
              v-if="canEdit(item)"
              class="kb-icon"
              :disabled="busy || (editorOpen && editingId !== item.id)"
              title="Изменить"
              @click="openEditor(item)"
            >
              <font-awesome-icon :icon="['fa-solid', 'fa-pen-to-square']" />
            </button>
            <button
              v-if="canDelete(item)"
              class="kb-icon del"
              :disabled="busy"
              title="Удалить"
              @click="removeItem(item)"
            >
              <font-awesome-icon :icon="['fa-solid', 'fa-trash']" />
            </button>
          </span>
        </div>
        <div v-if="openId === item.id && editingId !== item.id" class="kb-item-view">
          <AiMarkdown :text="item.text" :final="true" />
        </div>
        <KnowledgeEditor
          v-if="editingId === item.id"
          :can-manage="canManage"
          :busy="busy"
          :popular-tags="popularTags"
          :initial="item"
          kind-locked
          @save="saveEditor"
          @cancel="closeEditor"
        />
        </div>
      </template>
      </section>
  </div>
</template>

<script>
import {
  fetchAiStatus,
  fetchMemories,
  fetchMemoryTags,
  createMemory,
  patchMemory,
  deleteMemory,
  bulkDeleteMemories,
} from '@/backend/aiApi';
import AiMarkdown from './components/AiMarkdown.vue';
import KnowledgeEditor from './components/KnowledgeEditor.vue';

export default {
  name: 'AiKnowledgePage',
  components: { AiMarkdown, KnowledgeEditor },
  data() {
    return {
      canManage: false,
      statusLoaded: false,
      loadError: '',
      items: [],
      loading: false,
      busy: false,
      error: '',
      activeKind: 'doc',
      q: '',
      searchTimer: null,
      popularTags: [],
      selectedTags: [],
      openId: null,
      selection: [],
      editorOpen: false,
      editingId: null,
      editorError: '',
      draft: null,
    };
  },
  computed: {
    kindTabs() {
      return [
        { kind: 'doc', label: 'Документы' },
        { kind: 'public', label: 'Публичные заметки' },
        { kind: 'personal', label: 'Личные' },
      ];
    },
  },
  watch: {
    q() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(), 300);
    },
  },
  created() {
    fetchAiStatus()
      .then((status) => {
        this.canManage = !!(status && status.can_manage_memory);
        this.statusLoaded = true;
        return this.reload();
      })
      .catch((e) => {
        this.loadError = `Не удалось загрузить статус: ${e.message || e}`;
      });
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const params = { kind: this.activeKind };
        if (this.q.trim()) params.q = this.q.trim();
        const data = await fetchMemories(params);
        let items = (data && data.items) || [];
        if (this.selectedTags.length) {
          items = items.filter((i) => this.selectedTags.every((t) => (i.tags || []).includes(t)));
        }
        items.sort((a, b) => {
          if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
          const da = a.updated_at || a.created_at || '';
          const db = b.updated_at || b.created_at || '';
          return db < da ? -1 : (db > da ? 1 : 0);
        });
        this.items = items;
        this.clearSelection();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.error = detail || `Не удалось загрузить: ${e.message || e}`;
      } finally {
        this.loading = false;
      }
    },
    async loadTags() {
      try {
        const data = await fetchMemoryTags();
        this.popularTags = ((data && data.items) || []).slice(0, 12);
      } catch (e) {
        this.popularTags = [];
      }
    },
    async reload() {
      await Promise.all([this.load(), this.loadTags()]);
    },
    switchKind(kind) {
      if (this.activeKind === kind) return;
      this.activeKind = kind;
      this.openId = null;
      this.closeEditor();
      this.load();
    },
    toggleTag(tag) {
      const idx = this.selectedTags.indexOf(tag);
      if (idx >= 0) this.selectedTags.splice(idx, 1);
      else this.selectedTags.push(tag);
      this.load();
    },
    clearTags() {
      this.selectedTags = [];
      this.load();
    },
    canEdit(item) {
      return this.canManage || item.kind === 'personal';
    },
    canDelete(item) {
      return this.canManage || item.kind === 'personal';
    },
    canPin(item) {
      return (this.canManage && item.kind === 'public') || item.kind === 'personal';
    },
    openItem(item) {
      if (this.editingId === item.id) {
        this.closeEditor();
        return;
      }
      if (this.openId === item.id) {
        this.openId = null;
        return;
      }
      if (this.canEdit(item)) {
        this.openEditor(item);
        return;
      }
      this.openId = item.id;
    },
    toggleSelect(id) {
      const idx = this.selection.indexOf(id);
      if (idx >= 0) this.selection.splice(idx, 1);
      else this.selection.push(id);
    },
    clearSelection() {
      this.selection = [];
    },
    openEditor(item) {
      this.editorError = '';
      if (item) {
        this.editingId = item.id;
        this.draft = {
          title: item.title || '',
          text: item.text || '',
          kind: item.kind,
          pinned: !!item.pinned,
          tags: [...(item.tags || [])],
        };
      } else {
        this.editingId = null;
        this.draft = {
          title: '',
          text: '',
          kind: this.canManage ? (this.activeKind === 'personal' ? 'personal' : this.activeKind) : 'personal',
          pinned: false,
          tags: this.selectedTags.length ? [...this.selectedTags] : [],
        };
      }
      this.editorOpen = true;
    },
    closeEditor() {
      this.editorOpen = false;
      this.editingId = null;
      this.draft = null;
      this.editorError = '';
    },
    async saveEditor(payload) {
      const text = (payload && payload.text || '').trim();
      if (!text) return;
      this.busy = true;
      this.editorError = '';
      const body = {
        text,
        title: payload.title,
        tags: [...(payload.tags || [])],
      };
      if (!this.editingId) body.kind = payload.kind;
      if ((this.canManage && payload.kind === 'public') || payload.kind === 'personal') {
        body.pinned = payload.pinned;
      }
      try {
        if (this.editingId) await patchMemory(this.editingId, body);
        else await createMemory(body);
        this.closeEditor();
        await this.reload();
        if (!this.editingId) window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.editorError = detail || `Не удалось сохранить: ${e.message || e}`;
      } finally {
        this.busy = false;
      }
    },
    async togglePin(item) {
      this.busy = true;
      this.error = '';
      try {
        await patchMemory(item.id, { pinned: !item.pinned });
        await this.load();
      } catch (e) {
        this.error = `Не удалось изменить: ${e.message || e}`;
      } finally {
        this.busy = false;
      }
    },
    async removeItem(item) {
      this.busy = true;
      this.error = '';
      try {
        await deleteMemory(item.id);
        if (this.editingId === item.id) this.closeEditor();
        await this.reload();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.error = detail || `Не удалось удалить: ${e.message || e}`;
      } finally {
        this.busy = false;
      }
    },
    async bulkDelete() {
      if (!this.selection.length) return;
      this.busy = true;
      this.error = '';
      try {
        const result = await bulkDeleteMemories([...this.selection]);
        const deleted = (result && result.deleted) || [];
        const skipped = (result && result.skipped) || [];
        if (skipped.length) {
          this.error = `Удалено ${deleted.length}, пропущено ${skipped.length}`;
        }
        await this.reload();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.error = detail || `Не удалось удалить: ${e.message || e}`;
      } finally {
        this.busy = false;
      }
    },
    fmtDate(item) {
      return (item.updated_at || item.created_at || '').slice(0, 10);
    },
  },
};
</script>

<style scoped>
.ai-knowledge-page {
  padding: 16px 20px 40px;
}

.toolbar {
  margin-bottom: 16px;
}

.title-block h1 {
  margin: 0 0 4px;
  font-size: 22px;
}

.subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 12px;
}

.card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 0;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
}

.header-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
}

.kind-tabs {
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
}

.kind-tab {
  border: none;
  background: transparent;
  padding: 5px 12px;
  font-size: 13px;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
}

.kind-tab.active {
  background: #fff;
  color: #111827;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.kb-search {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  width: 240px;
}

.btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: #f9fafb;
}

.btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn.danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

.btn.danger:hover:not(:disabled) {
  background: #b91c1c;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.field-hint {
  font-size: 11px;
  color: #9ca3af;
}

.empty-hint {
  padding: 16px 0;
}

.badge {
  font-size: 11px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
}

.tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.tag-row-label {
  font-size: 12px;
  color: #9ca3af;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.tag-chip:hover {
  border-color: #2563eb;
  color: #2563eb;
}

.tag-chip.active {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}

.tag-chip.mini {
  padding: 1px 8px;
  font-size: 11px;
  cursor: pointer;
}

.tag-count {
  color: #9ca3af;
  font-size: 10px;
}

.tag-clear {
  margin-left: 4px;
}

.bulk-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
}

.kb-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.kb-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  cursor: pointer;
}

.kb-item-top:hover .kb-item-title {
  color: #2563eb;
}

.kb-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 13px;
  padding: 8px 0;
}

.kb-loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: kb-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes kb-spin {
  to {
    transform: rotate(360deg);
  }
}

.kb-check input {
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.kb-item-title {
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.kb-item-date {
  color: #9ca3af;
  font-size: 11px;
  margin-left: auto;
}

.kb-item-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.kb-icon {
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
  padding: 3px 4px;
  border-radius: 4px;
}

.kb-icon:hover:not(:disabled) {
  color: #2563eb;
  background: #eff6ff;
}

.kb-icon.active {
  color: #2563eb;
}

.kb-icon.del:hover:not(:disabled) {
  color: #dc2626;
  background: #fef2f2;
}

.kb-icon:disabled {
  opacity: 0.5;
  cursor: default;
}

.kb-act {
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}

.kb-act:hover:not(:disabled) {
  color: #2563eb;
}

.kb-act.del:hover:not(:disabled) {
  color: #dc2626;
}

.kb-act:disabled {
  opacity: 0.5;
  cursor: default;
}

.kb-item-view {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e7eb;
  max-height: 420px;
  overflow-y: auto;
}
</style>
